import React, { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import {
  Alert,
  Box,
  Button,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  FormLabel,
  IconButton,
  Snackbar,
  Switch,
  Typography,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { RiCheckboxCircleLine, RiCloseLine } from "@remixicon/react";
import { format } from "date-fns";
import pluralize from "pluralize";

import { AuthContextType, useAuth } from "../../../contexts/AuthContext";
import {
  DeliveryItemT,
  RecurringRemovePayload_T,
  RecurringRemoveResponse_T,
  ScheduleDialogProps,
} from "../../../types/ScheduleForm";
import useGaneshApi from "../../../hooks/useGaneshApi";
import { generateFormLabelTypographyProps } from "./FormLabelProps";
import RecurringSummaryCounts from "./RecurringSummaryCounts";
import DeliveriesCombo from "./DeliveriesCombo";

export const RecurringRemoveDialog: React.FC<ScheduleDialogProps> = ({
  onClose,
  selectedRows,
  action,
  onSuccessCallback,
}) => {
  const userAuth: AuthContextType = useAuth();
  const { apiRequest } = useGaneshApi();
  const theme = useTheme();
  const formLabelTypographyProps = generateFormLabelTypographyProps(theme);

  const startDate = format(new Date().toISOString(), "yyyy-MM-dd");
  const [selectedItems, setSelectedItems] = useState<DeliveryItemT[]>(
    selectedRows ?? []
  );
  const [removeAnother, setRemoveAnother] = useState<boolean>(false);
  const [isValidRemoval, setIsValidRemoval] = useState<boolean>(false);
  const [snackbarOpen, setSnackbarOpen] = useState<boolean>(false);
  const [snackbarMessage, setSnackbarMessage] = useState<string>("");

  const { mutate: deleteRecurring, isPending } = useMutation({
    mutationFn: async (payload: RecurringRemovePayload_T) => {
      const response = await apiRequest<RecurringRemoveResponse_T>(
        "/schedule/batch-remove-deliveries-date-range",
        {
          method: "DELETE",
          body: payload,
        }
      );
      return response;
    },
    onSuccess: (data, variables) => {
      if (removeAnother) {
        setSnackbarOpen(true);
        setSnackbarMessage("Recurring deliveries removed successfully.");
        setIsValidRemoval(false);
        setSelectedItems([]);
      } else {
        onClose();
        onSuccessCallback?.();
      }
    },
    onError: (error) => {
      console.error(error);
    },
  });

  const handleSubmit = async () => {
    const payload: RecurringRemovePayload_T = {
      admin_id: userAuth.authAttributes?.id ?? null,
      dateStart: startDate,
      delivery_ids: selectedItems.map((item) => item.delivery_id),
    };

    deleteRecurring(payload);
  };

  return (
    <>
      <DialogTitle>
        Remove recurring deliveries
        <IconButton
          aria-label="close"
          onClick={onClose}
          style={{ position: "absolute", right: 8, top: 8 }}
        >
          <RiCloseLine />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <form
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 2fr",
            gap: "24px",
            alignItems: "start",
            width: "100%",
            margin: "0 auto",
          }}
        >
          <FormLabel
            {...formLabelTypographyProps}
            sx={{ ...formLabelTypographyProps.sx, mt: 1 }}
          >
            Deliveries *
          </FormLabel>
          <DeliveriesCombo
            selectedItems={selectedItems}
            onSelectionChange={() => {}}
            action={action}
          />
          <FormLabel component="legend" {...formLabelTypographyProps}>
            Summary Counts
          </FormLabel>
          <Box>
            <RecurringSummaryCounts
              startDate={startDate}
              selectedItems={selectedItems.map((item) => item.delivery_id)}
              isRemoving={true}
              setIsValidRemoval={setIsValidRemoval}
            />
          </Box>
          <DialogActions
            style={{
              gridColumn: "1 / -1",
              justifyContent: "end",
              marginTop: "16px",
            }}
          >
            <FormControlLabel
              control={
                <Switch
                  checked={removeAnother}
                  onChange={(e) => setRemoveAnother(e.target.checked)}
                  name="removeAnother"
                  size="small"
                />
              }
              label="Remove more"
            />
            <Button
              variant="contained"
              color="error"
              onClick={handleSubmit}
              sx={{ minWidth: "120px" }}
              disabled={
                selectedItems.length === 0 || !isValidRemoval || isPending
              }
            >
              {isPending ? "Removing" : "Remove"}{" "}
              {pluralize("delivery", selectedItems.length)}
            </Button>
          </DialogActions>
        </form>
        <Snackbar
          open={snackbarOpen}
          autoHideDuration={6000}
          message={snackbarMessage}
          onClose={() => {
            setSnackbarOpen(false);
            setSnackbarMessage("");
          }}
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        >
          <Alert
            onClose={() => {
              setSnackbarOpen(false);
              setSnackbarMessage("");
            }}
            severity="success"
            sx={{ width: "100%" }}
            icon={<RiCheckboxCircleLine size={24} />}
          >
            <Box>
              <Typography variant="body1">{snackbarMessage}</Typography>
            </Box>
          </Alert>
        </Snackbar>
      </DialogContent>
    </>
  );
};
